// Conversation copy and export utilities
import { ChatMessage, Conversation } from '../api/models';
import { authenticatedFetch } from './authApi';

/**
 * Copy text to clipboard with user feedback
 */
export const copyToClipboard = async (text: string, onSuccess?: () => void, onError?: (error: string) => void): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    onSuccess?.();
  } catch (error) {
    const errorMessage = `Failed to copy: ${error instanceof Error ? error.message : 'Unknown error'}`;
    onError?.(errorMessage);
    console.error('Copy to clipboard failed:', error);
  }
};

/**
 * Get the rendered/formatted content from a message instead of raw markdown
 */
export const getFormattedMessageContent = (message: ChatMessage): string => {
  if (typeof message.content !== 'string') {
    return JSON.stringify(message.content);
  }

  let content = message.content;
  
  // Convert markdown to plain text with preserved formatting
  // Remove markdown syntax while keeping structure
  content = content
    // Headers - convert to text with line breaks
    .replace(/^#{1,6}\s+(.+)$/gm, '$1\n' + '='.repeat(20) + '\n')
    // Bold and italic - remove markers but keep emphasis with caps for bold
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Code blocks - keep content, remove markers
    .replace(/```[\s\S]*?```/g, (match) => {
      const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '');
      return `\n--- Code ---\n${code}\n--- End Code ---\n`;
    })
    // Inline code
    .replace(/`([^`]+)`/g, '$1')
    // Links - keep URL and text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    // Lists - convert to readable format
    .replace(/^[\s]*[\-\*\+]\s+/gm, '• ')
    .replace(/^[\s]*\d+\.\s+/gm, (match, offset, string) => {
      const num = match.match(/\d+/)?.[0] || '1';
      return `${num}. `;
    })
    // Horizontal rules
    .replace(/^[\s]*[-=]{3,}[\s]*$/gm, '\n' + '-'.repeat(40) + '\n')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return content;
};

/**
 * Format a single message for copying
 */
export const formatMessageForCopy = (message: ChatMessage): string => {
  const timestamp = new Date(message.date).toLocaleString();
  const role = message.role === 'user' ? 'User' : message.role === 'assistant' ? 'Assistant' : 'System';
  const content = getFormattedMessageContent(message);
  
  return `[${timestamp}] ${role}:\n${content}\n`;
};

/**
 * Format an entire conversation for copying
 */
export const formatConversationForCopy = (conversation: Conversation): string => {
  const header = `Conversation: ${conversation.title}\nDate: ${new Date(conversation.date).toLocaleString()}\n`;
  const divider = '='.repeat(50) + '\n\n';
  
  const messages = conversation.messages
    .filter(msg => msg.role === 'user' || msg.role === 'assistant') // Filter out system messages
    .map(formatMessageForCopy)
    .join('\n');
  
  return header + divider + messages;
};

/**
 * Get only assistant message content for copying (clean format)
 */
export const getAssistantContentForCopy = (message: ChatMessage): string => {
  if (message.role !== 'assistant') return '';
  return getFormattedMessageContent(message);
};

/**
 * Export conversation to file (PDF or DOCX)
 */
export const exportConversation = async (
  conversation: Conversation, 
  format: 'pdf' | 'docx',
  onSuccess?: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  try {
    const content = formatConversationForCopy(conversation);
    
    // Clean title: keep letters (including Hebrew/Unicode), numbers, spaces
    const cleanTitle = (conversation.title || 'Conversation')
      .replace(/[<>:"/\\|?*]/g, '')  // Remove unsafe file chars
      .replace(/\s+/g, ' ')          // Collapse whitespace
      .trim()
      .substring(0, 50) || 'Conversation';
    
    const timestamp = new Date().toISOString().slice(0, 10);  // YYYY-MM-DD
    const title = `${cleanTitle}_${timestamp}`;
    
    const response = await authenticatedFetch('/document/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        content, 
        title, 
        format: format === 'docx' ? 'word' : 'pdf' 
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${errorText}`);
    }

    const blob = await response.blob();
    const ext = format === 'docx' ? 'docx' : 'pdf';
    const filename = `${title}.${ext}`;
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    onSuccess?.();
  } catch (error) {
    const errorMessage = `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    onError?.(errorMessage);
    console.error('Conversation export failed:', error);
  }
};

/**
 * Export single message to file (PDF or DOCX)
 * If the message contains visuals (charts/dashboards), captures them as images
 */
export const exportMessage = async (
  message: ChatMessage, 
  format: 'pdf' | 'docx',
  conversationTitle?: string,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  messageElementId?: string  // DOM element ID containing the message for visual capture
): Promise<void> => {
  try {
    const content = formatMessageForCopy(message);
    
    // Clean title: keep letters (including Hebrew/Unicode), numbers, spaces
    // Replace unsafe file chars, collapse multiple spaces/underscores
    const cleanTitle = (conversationTitle || 'Response')
      .replace(/[<>:"/\\|?*]/g, '')  // Remove unsafe file chars
      .replace(/\s+/g, ' ')          // Collapse whitespace
      .trim()
      .substring(0, 50) || 'Response';
    
    // Generate a readable timestamp for uniqueness
    const timestamp = new Date().toISOString().slice(0, 10);  // YYYY-MM-DD
    const title = `${cleanTitle}_${timestamp}`;
    
    // Try to capture any visual content (dashboard, chart) from the message container
    let imageData: string | null = null;
    
    // Look for the message container by ID or find dashboard/chart elements
    const messageContainer = messageElementId 
      ? document.getElementById(messageElementId)
      : document.querySelector(`[data-message-id="${message.id}"]`);
    
    console.log('[Export] Looking for message container with id:', message.id);
    console.log('[Export] Found container:', !!messageContainer);
    
    if (messageContainer) {
      // Look for dashboard or chart within this message
      // Use data attributes for reliability, plus CSS class fallbacks
      const visualElement = messageContainer.querySelector('[data-visual="dashboard"], [data-visual="chart"], [class*="dashboard"], [class*="Dashboard"], [class*="chart"], [class*="Chart"], .recharts-wrapper, canvas');
      
      console.log('[Export] Found visual element:', !!visualElement, visualElement?.className);
      
      if (visualElement && visualElement instanceof HTMLElement) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(visualElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
          });
          imageData = canvas.toDataURL('image/png');
          console.log('[Export] Captured visual content for export, size:', imageData.length);
        } catch (captureError) {
          console.warn('[Export] Could not capture visual content:', captureError);
        }
      }
    } else {
      console.log('[Export] No message container found for message id:', message.id);
    }
    
    // Build request with optional image
    const requestBody: any = { 
      content, 
      title, 
      format: format === 'docx' ? 'word' : 'pdf' 
    };
    
    if (imageData) {
      requestBody.imageData = imageData;
    }
    
    const response = await authenticatedFetch('/document/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${errorText}`);
    }

    const blob = await response.blob();
    const ext = format === 'docx' ? 'docx' : 'pdf';
    const filename = `${title}.${ext}`;
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    onSuccess?.();
  } catch (error) {
    const errorMessage = `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    onError?.(errorMessage);
    console.error('Message export failed:', error);
  }
};

/**
 * Show success message briefly in UI
 */
export const showCopySuccess = (element?: HTMLElement) => {
  if (!element) return;
  
  const originalText = element.textContent;
  element.textContent = 'Copied!';
  element.style.color = '#16A085';
  
  setTimeout(() => {
    element.textContent = originalText;
    element.style.color = '';
  }, 1500);
};

/**
 * Format conversation messages for better readability
 */
export const formatConversationForDisplay = (conversation: Conversation): string => {
  const header = `📄 ${conversation.title}\n🕒 ${new Date(conversation.date).toLocaleString()}\n`;
  const divider = '━'.repeat(50) + '\n\n';
  
  const messages = conversation.messages
    .filter(msg => msg.role === 'user' || msg.role === 'assistant')
    .map((msg, index) => {
      const timestamp = new Date(msg.date).toLocaleString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const role = msg.role === 'user' ? '👤 You' : '🤖 Assistant';
      const content = getFormattedMessageContent(msg);
      
      return `${role} (${timestamp}):\n${content}\n`;
    })
    .join('\n' + '─'.repeat(30) + '\n\n');
  
  return header + divider + messages;
};